import { graphql } from "babel-plugin-relay/macro";
import { useSelector } from "react-redux";
import { useFragment } from "react-relay";
import styled from "styled-components";
import { WissenwirktAttachmentHeftDisplay_LearnOpportunityV2Fragment$key } from "@generated/WissenwirktAttachmentHeftDisplay_LearnOpportunityV2Fragment.graphql";
import { WissenwirktAttachmentButtonsDisplay } from "./WissenwirktAttachmentButtonsDisplay";
import { useTypedSelector } from "../../../infecto-lms-webapp/redux/redux-store";
import {
	selectJwtClaimData,
	selectLoginData,
} from "../../../infecto-lms-webapp/redux/slices/auth.slice";
import { RefreshToken } from "../../../infecto-lms-webapp/redux/slices/RefreshToken";
import { LMS_WEBAPP_CONFIG_IMPL } from "../../../infecto-lms-webapp-impl/config";
import { DownloadIcon } from "../nodecard/SvgNodeIcons.component";

const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment WissenwirktAttachmentHeftDisplay_LearnOpportunityV2Fragment on LearnOpportunityV2 {
		id
		structureDefinition {
			... on LearnOpportunityRootStructureDefinition {
				extension {
					... on LearnOpportunityRootExtensionImpl {
						firstLearnOpportunityIdWithMultipleChoiceElement
					}
				}
			}
		}
		root {
			id
			structureDefinition {
				... on LearnOpportunityRootStructureDefinition {
					extension {
						... on LearnOpportunityRootExtensionImpl {
							firstLearnOpportunityIdWithMultipleChoiceElement
						}
					}
				}
			}
			attachmentsV2 {
				... on InfectopharmFileAttachment {
					file {
						url
					}
				}
			}
			typeDefinition {
				... on LearnOpportunityBranchTypeDefinition {
					children {
						id
						attachmentsV2 {
							... on InfectopharmFileAttachment {
								file {
									url
								}
							}
						}
					}
				}
			}
		}
		...WissenwirktAttachmentButtonsDisplayFragment_LearnOpportunityV2Fragment
	}
`;

interface OwnProps {
	learnOpportunityFragmentRef: WissenwirktAttachmentHeftDisplay_LearnOpportunityV2Fragment$key;
	marginTopValue: string;
}

export function WissenwirktAttachmentHeftDisplay({
	learnOpportunityFragmentRef,
	marginTopValue,
}: OwnProps) {
	const loginData = useTypedSelector(selectLoginData);
	const jwtClaimData = useSelector(selectJwtClaimData);
	const learnOpportunity =
		useFragment<WissenwirktAttachmentHeftDisplay_LearnOpportunityV2Fragment$key>(
			LEARN_OPPORTUNITY_FRAGMENT,
			learnOpportunityFragmentRef,
		);
	const nodeId = learnOpportunity.root?.id
		? learnOpportunity?.root?.structureDefinition.extension
				?.firstLearnOpportunityIdWithMultipleChoiceElement
		: learnOpportunity?.structureDefinition.extension
				?.firstLearnOpportunityIdWithMultipleChoiceElement;

	return (
		<WrapperContainer marginTop={marginTopValue}>
			{learnOpportunity.structureDefinition.extension
				?.firstLearnOpportunityIdWithMultipleChoiceElement ||
			learnOpportunity.root?.structureDefinition.extension
				?.firstLearnOpportunityIdWithMultipleChoiceElement ||
			learnOpportunity?.root?.attachmentsV2.length ? (
				<div>
					<Title className="text-base mb-1">Download</Title>
					<div className="flex flex-column w-full justify-content-center">
						<HeftContainer>
							{(learnOpportunity.structureDefinition.extension
								?.firstLearnOpportunityIdWithMultipleChoiceElement ||
								learnOpportunity.root?.structureDefinition.extension
									?.firstLearnOpportunityIdWithMultipleChoiceElement) && (
								<AttachmentButton
									onClick={() => {
										RefreshToken(
											loginData?.refreshToken as string,
											jwtClaimData?.accountId as string,
										).then((currentAccessToken) => {
											window.open(
												`${process.env.REACT_APP_API_BASE}/api/download-cme-questions?token=${currentAccessToken}&accountId=${jwtClaimData?.accountId}&nodeId=${nodeId}`,
											);
										});
									}}
								>
									<AttachmentLabel>
										{LMS_WEBAPP_CONFIG_IMPL.multipleChoiceDownloadButtonText.toUpperCase()}
									</AttachmentLabel>
									<DownloadIcon />
								</AttachmentButton>
							)}
							<WissenwirktAttachmentButtonsDisplay learnOpportunityFragmentRef={learnOpportunity} />
						</HeftContainer>
					</div>
				</div>
			) : null}
		</WrapperContainer>
	);
}

interface MarginProps {
	marginTop: string;
}

const WrapperContainer = styled.div<MarginProps>`
	margin-top: ${(props) => props.marginTop};
`;

const HeftContainer = styled.div`
	display: flex;
	flex-direction: column;
`;

const Title = styled.div`
	text-align: center;
	font-size: 16px;
	font-style: normal;
	font-weight: 600;
	line-height: 24px;
	letter-spacing: 0.96px;
`;

export const AttachmentButton = styled.div`
	border-radius: 4px;
	border: 1px solid #000;
	background: rgba(79, 175, 68, 0);
	display: flex;
	width: 100%;
	padding: 16px 24px;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 10px;
	text-align: center;
	cursor: pointer;
	margin-bottom: 8px;

	&:hover {
		background-color: #efeff0;
	}
`;

export const AttachmentLabel = styled.span`
	color: #000;
	text-align: center;
	font-size: 16px;
	font-style: normal;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.96px;
`;
